import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useAuth, API } from "@/App";
import { Mail, Lock, User, ArrowRight, BarChart3 } from "lucide-react";

export default function LoginPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: ""
  });
  
  const { login, user } = useAuth();
  const navigate = useNavigate();

  if (user) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? "/auth/login" : "/auth/register";
      const payload = isLogin 
        ? { email: formData.email, password: formData.password }
        : formData;

      const response = await axios.post(`${API}${endpoint}`, payload);
      
      login(response.data.access_token, response.data.user);
      toast.success(isLogin ? "Přihlášení úspěšné" : "Registrace úspěšná");
      navigate("/");
    } catch (error) {
      const message = error.response?.data?.detail || "Došlo k chybě";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1646310997905-14eb66d1e04a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGRhdGElMjB2aXN1YWxpemF0aW9uJTIwZGFyayUyMGJsdWUlMjBhbmQlMjB5ZWxsb3clMjBtaW5pbWFsaXN0fGVufDB8fHx8MTc3MTQyMjUyMHww&ixlib=rb-4.1.0&q=85')`
        }}
      >
        <div className="absolute inset-0 bg-[#0F172A]/80"></div>
        <div className="relative z-10 flex flex-col justify-center p-12 text-white">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-[#FACC15] flex items-center justify-center">
              <BarChart3 className="w-7 h-7 text-[#0F172A]" />
            </div>
            <span className="font-oswald text-3xl font-bold tracking-tight">WELEAD INSIGHT</span>
          </div>
          <h1 className="font-oswald text-5xl font-bold tracking-tight uppercase mb-6">
            Data-Driven<br />Decisions
          </h1>
          <p className="font-serif text-xl text-white/80 leading-relaxed max-w-md">
            Transformujte vaše e-commerce data na strategické insighty. 
            Analyzujte retention, LTV a zákaznickou strukturu během sekund.
          </p>
          <div className="mt-12 flex gap-8">
            <div>
              <p className="font-oswald text-4xl font-bold text-[#FACC15]">10k+</p>
              <p className="text-white/60 font-sans text-sm">Analyzovaných zákazníků</p>
            </div>
            <div>
              <p className="font-oswald text-4xl font-bold text-[#FACC15]">500+</p>
              <p className="text-white/60 font-sans text-sm">Vygenerovaných reportů</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[#F8FAFC]">
        <Card className="w-full max-w-md border-0 shadow-lg bg-white">
          <CardHeader className="space-y-2 pb-6">
            <div className="lg:hidden flex items-center gap-2 mb-4">
              <div className="w-10 h-10 bg-[#0F172A] flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-[#FACC15]" />
              </div>
              <span className="font-oswald text-xl font-bold text-[#0F172A]">WELEAD INSIGHT</span>
            </div>
            <CardTitle className="font-oswald text-3xl text-[#0F172A] tracking-tight">
              {isLogin ? "PŘIHLÁŠENÍ" : "REGISTRACE"}
            </CardTitle>
            <CardDescription className="font-sans text-[#475569]">
              {isLogin 
                ? "Přihlaste se pro přístup k reportům" 
                : "Vytvořte si účet pro generování reportů"
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="name" className="font-sans text-[#0F172A] font-medium">
                    Jméno
                  </Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
                    <Input
                      id="name"
                      data-testid="register-name-input"
                      placeholder="Vaše jméno"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="pl-10 h-12 border-[#E2E8F0] focus:border-[#FACC15] focus:ring-[#FACC15] rounded-sm"
                      required={!isLogin}
                    />
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="email" className="font-sans text-[#0F172A] font-medium">
                  Email
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
                  <Input
                    id="email"
                    type="email"
                    data-testid="login-email-input"
                    placeholder="vas@email.cz"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="pl-10 h-12 border-[#E2E8F0] focus:border-[#FACC15] focus:ring-[#FACC15] rounded-sm"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="font-sans text-[#0F172A] font-medium">
                  Heslo
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
                  <Input
                    id="password"
                    type="password"
                    data-testid="login-password-input"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="pl-10 h-12 border-[#E2E8F0] focus:border-[#FACC15] focus:ring-[#FACC15] rounded-sm"
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                data-testid="login-submit-btn"
                disabled={loading}
                className="w-full h-12 bg-[#0F172A] hover:bg-[#1E293B] text-white font-oswald text-lg tracking-wide uppercase rounded-sm transition-all duration-200"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    {isLogin ? "Přihlásit se" : "Registrovat"}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button
                type="button"
                data-testid="toggle-auth-mode-btn"
                onClick={() => setIsLogin(!isLogin)}
                className="font-sans text-[#475569] hover:text-[#0F172A] transition-colors"
              >
                {isLogin ? (
                  <>Nemáte účet? <span className="font-semibold text-[#0F172A]">Registrujte se</span></>
                ) : (
                  <>Již máte účet? <span className="font-semibold text-[#0F172A]">Přihlaste se</span></>
                )}
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
